import { Button, Checkbox, Form, Input, Select } from 'antd';
import * as _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import config from '../../../config/config';
import iconChevronRightUrl from '../../assets/icon/chevron-right.svg';
import iconWarningUrl from '../../assets/icon/warning.svg';
import {
  checkPhoneExistAction,
  requestNewOTPAction,
  resetStateAction,
  storeBizAccountCustomerCode
} from '../../store/login/actions';
import {
  OTP_CONFIRMED,
  OTP_NO_CONFIRMED,
  PASSWORD_EXIST,
  PHONE_NOT_EXIST
} from '../../store/login/actionTypes';
import {
  getCheckPhoneExistStatus,
  getLoginMessage,
  getLoginStatus
} from '../../store/login/selectors';
import { LANGUAGE_KEY } from '../../utilities/constant';

const initialMsgState = {
	phoneNumber: '',
	term: '',
};

const ValidatePhoneNumber = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [form] = Form.useForm();
	const [msg, setMsg] = useState(initialMsgState);
	const [isPersonal, setIsPersonal] = useState(true);
	const status = useSelector(getCheckPhoneExistStatus);
	const loginMessage = useSelector(getLoginMessage);
	const loginStatus = useSelector(getLoginStatus);
	const [isBizAccount, setIsBizAccount] = useState(false);
	const { t, i18n } = useTranslation();
	const { Option } = Select;
	const accounts = [
		{
			label: 'personal_account',
			value: 'personal',
		},
		{
			label: 'business_account',
			value: 'business',
		},
	];

	const handleChangeAccount = (e: any) => {
		setIsPersonal(e.target.value === 'personal');
		form.resetFields(['phoneNumber']);
		setMsg({ phoneNumber: '', term: '' });
	};

	useEffect(() => {
		if (loginStatus) {
			navigate('/');
		} else {
			dispatch(resetStateAction());
		}
	}, [loginStatus]);

	const handleFormChange = () => {
		validateForm();
	};

	const validateForm = () => {
		const { phoneNumber, term } = form.getFieldsValue();
		setMsg({ phoneNumber: '', term: '' });

		if (!phoneNumber || !phoneNumber.length) {
			const msgErr = isPersonal
				? 'validation_required_phone'
				: 'validation_required_customer';
			setMsg({ ...msg, phoneNumber: msgErr });
			return false;
		}

		let isValid = true;
		// check biz account
		if (!isPersonal) {
			setIsBizAccount(true);
			return true;
		}

		setIsBizAccount(false);
		if (phoneNumber.length < 9) {
			setMsg({ ...msg, phoneNumber: 'validation_phone_length_min' });
			isValid = false;
		}
		if (!term) {
			setMsg({ ...msg, term: 'validation_term_service' });
			isValid = false;
		}
		return isValid;
	};

	const getPhoneNumberValue = () => {
		const { prefix, phoneNumber } = form.getFieldsValue();
		let phone = phoneNumber;
		if (isPersonal) {
			if (_.startsWith(phoneNumber, '0')) {
				phone = phoneNumber.substring(1);
			}
			phone = prefix + phone;
		}
		return phone;
	};

	const handleOnSubmit = () => {
		if (validateForm()) {
			// call biz account
			dispatch(checkPhoneExistAction(getPhoneNumberValue()));
		}
	};

	useEffect(() => {}, [loginMessage]);

	useEffect(() => {
		switch (status) {
			case PHONE_NOT_EXIST:
				// new customer account, redirect to OTP page
				// OTP auto send from api check_phone
				// biz account not exist, only show message, not redirect
				if (!isBizAccount) {
					navigate('/enter-otp');
				}
				break;
			case OTP_NO_CONFIRMED:
			case OTP_CONFIRMED:
				// phone is invalid, move to OTP to create new account
				const phoneValue = getPhoneNumberValue();
				if (isBizAccount) {
					phoneValue &&
						dispatch(
							storeBizAccountCustomerCode({
								customerCode: phoneValue,
							}),
						);
				}
				// request new OTP here
				phoneValue && dispatch(requestNewOTPAction(phoneValue));
				navigate('/enter-otp');
				break;
			case PASSWORD_EXIST:
				// phone is valid, move to step 2 to enter password
				navigate('/enter-password');
				break;
			default:
				break;
		}
	}, [status]);

	const registerNow = () => {
		navigate('/register');
	};

	const prefixSelector = (
		<Form.Item name="prefix" noStyle>
			<Select
				style={{
					width: 70,
				}}
			>
				<Option value="82">+82</Option>
				<Option value="84">+84</Option>
			</Select>
		</Form.Item>
	);

	return (
		<>
			<div className="header-title">
				<div className="title">{t('global_welcome')}</div>
				<div className="subtitle">{t('section_title_login')}</div>
			</div>
			<Form
				layout="vertical"
				initialValues={{ term: true, prefix: '84' }}
				onValuesChange={() => handleFormChange()}
				form={form}
			>
				<Form.Item className="account-form">
					<div className="radio-button-group mts">
						{accounts.map((account, i) => {
							return (
								<div className="item">
									<input
										type="radio"
										name="button-group"
										className="radio-button"
										value={account.value}
										id={account.value}
										onChange={handleChangeAccount}
										defaultChecked={i === 0}
									/>
									<label htmlFor={account.value}>
										{t(account.label)}
									</label>
								</div>
							);
						})}
					</div>
				</Form.Item>
				<Form.Item
					className="phone-form"
					help={t(msg.phoneNumber)}
					name="phoneNumber"
				>
					<div>
						<Input
							addonBefore={isPersonal ? prefixSelector : null}
							placeholder={t(
								isPersonal
									? 'placeholder_phone'
									: 'placeholder_enter_customer_code',
							)}
							type="number"
							onKeyPress={(e: any) => {
								const lengthPhone = isPersonal ? 11 : 8;
								if (e.target.value.length === lengthPhone) {
									e.preventDefault();
									return false;
								}
							}}
							min={0}
							maxLength={8}
							autoFocus
						/>
						{loginMessage && (
							<div className="error-msg">
								<img src={iconWarningUrl} />{' '}
								<span>{t(loginMessage)}</span>
							</div>
						)}
					</div>
				</Form.Item>

				<Form.Item>
					<Button
						type="primary"
						htmlType="submit"
						onClick={handleOnSubmit}
            onKeyUp={e => {
              if (e.key === 'Enter') {
                handleOnSubmit();
              }
            }}
					>
						{t('button_next')}{' '}
						<img
							src={iconChevronRightUrl}
							width="16"
							className="ms-1"
						/>
					</Button>
				</Form.Item>

				<Form.Item
					help={t(msg.term)}
					valuePropName="checked"
					name="term"
				>
					<Checkbox>
						{t('authentication_message_term_service_1')}{' '}
						<a
							target="_blank"
							className="term-link"
							href={
								config.termCondition[
									(i18n.language || 'en') as LANGUAGE_KEY
								]
							}
						>
							{t('authentication_message_term_service_2')}
						</a>{' '}
						{t('authentication_message_term_service_3')}{' '}
						<a
							target="_blank"
							className="term-link"
							href={
								config.privatePolicy[
									(i18n.language || 'en') as LANGUAGE_KEY
								]
							}
						>
							{t('authentication_message_term_service_4')}
						</a>
					</Checkbox>
				</Form.Item>

					<Form.Item style={{visibility: isPersonal ? 'hidden' : 'visible'}}>
						<b>{t('login_message_business_account_hint')} </b>
						<a
							className="text-decoration-underline"
							onClick={() => registerNow()}
						>
							{t('login_message_business_account_register')}
						</a>
					</Form.Item>

			</Form>
		</>
	);
};
export default ValidatePhoneNumber;
